import { isStaleAccountError } from '@/lib/stale-account';
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Bell,
  BellRing,
  Briefcase,
  MessageSquare,
  DollarSign,
  ShoppingBag,
  Eye,
  Check,
  CheckCheck,
  Calendar,
  CalendarCheck,
  CalendarX,
} from "lucide-react";
import { notificationService, type BaseNotification } from "@/lib/services/notifications/notification.service";
import { useFirebaseAuth } from "@/hooks/use-firebase-auth";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { isExternalUrl, resolveNotificationRoute } from "@/lib/notifications/resolve-notification-route";

export function NotificationsDropdown() {
  const { user } = useFirebaseAuth();
  const [, setLocation] = useLocation();
  const [notifications, setNotifications] = useState<BaseNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Real-time subscription to notifications
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    setLoading(true);
    // Answers that arrive after this account is gone are dropped.
    let active = true;

    // Timeout fallback - stop loading after 5 seconds and try static method
    const timeoutId = setTimeout(async () => {
      console.warn('⚠️ Notifications loading timeout - trying static method');
      try {
        const staticNotifications = await notificationService.getNotifications(user.id);
        if (!active) return;
        setNotifications(staticNotifications);
        const unreadCount = staticNotifications.filter(n => !n.read).length;
        setUnreadCount(unreadCount);
      } catch (error) {
        // Background, non-critical fetch. It fails transiently during the
        // auth-restore race (token not ready → permission-denied) and after a
        // deploy (stale chunk). Do NOT nag the user with a red toast while
        // they're mid-action (e.g. adding to cart) — that was the "a lot of
        // failed to fetch notifications" complaint. Keep the console/Sentry
        // trail; the real-time subscription recovers once auth settles.
        if (!active || isStaleAccountError(error)) return;
        console.error('❌ Static notifications fetch failed:', error);
      }
      setLoading(false);
    }, 5000);

    const unsubscribe = notificationService.subscribeToNotifications(
      user.id,
      (newNotifications) => {
        console.log('📢 Notifications received:', newNotifications.length, newNotifications);
        clearTimeout(timeoutId); // Clear timeout since we got data
        setNotifications(newNotifications);
        const unreadCount = newNotifications.filter(n => !n.read).length;
        setUnreadCount(unreadCount);
        setLoading(false);
      }
    );

    // Cleanup subscription and timeout on unmount
    return () => {
      active = false;
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, [user?.id]);

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));

      toast.success('Notification marked as read');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;

    try {
      await notificationService.markAllAsRead(user.id);

      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);

      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = async (notification: BaseNotification) => {
    // Mark as read if not already read
    if (!notification.read && notification.id) {
      await handleMarkAsRead(notification.id);
    }

    // ALL routing decisions go through resolveNotificationRoute — a single
    // source of truth that handles type normalization (camelCase /
    // SCREAMING_CASE / snake_case), absolute-URL stripping, stale-path
    // rewrites (`/orders/<id>` → `/orders`, `/dashboard/purchases` →
    // `/orders`, etc.), and per-type fallbacks from `notification.data`.
    // Branching here would re-introduce the drift this skill was written
    // to prevent.
    const destination = resolveNotificationRoute(notification);
    if (destination) {
      if (isExternalUrl(destination)) {
        window.location.assign(destination);
      } else {
        setLocation(destination);
      }
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'jobApplication':
      case 'jobAcceptance':
      case 'jobRejection':
        return <Briefcase className="h-4 w-4" />;
      case 'connectRequest':
        return <Calendar className="h-4 w-4" />;
      case 'connectConfirmed':
        return <CalendarCheck className="h-4 w-4" />;
      case 'connectCancelled':
        return <CalendarX className="h-4 w-4" />;
      case 'message':
        return <MessageSquare className="h-4 w-4" />;
      case 'profileView':
        return <Eye className="h-4 w-4" />;
      case 'purchaseConfirmation':
        return <ShoppingBag className="h-4 w-4" />;
      case 'saleNotification':
      case 'payment':
        return <DollarSign className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'jobApplication':
        return 'text-blue-500';
      case 'jobAcceptance':
        return 'text-green-500';
      case 'jobRejection':
        return 'text-red-500';
      case 'connectRequest':
        return 'text-orange-500';
      case 'connectConfirmed':
        return 'text-green-500';
      case 'connectCancelled':
        return 'text-red-500';
      case 'message':
        return 'text-purple-500';
      case 'profileView':
        return 'text-cyan-500';
      case 'purchaseConfirmation':
        return 'text-blue-500';
      case 'saleNotification':
        return 'text-emerald-500';
      case 'payment':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const formatTimeAgo = (timestamp: any) => {
    try {
      let date: Date;

      if (timestamp?.seconds) {
        // Firestore Timestamp
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'Recently';
      }

      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
      if (diffInHours < 24) return `${diffInHours}h ago`;
      if (diffInDays < 7) return `${diffInDays}d ago`;

      return date.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative text-white hover:text-cyan-400 p-2"
        >
          {unreadCount > 0 ? (
            <BellRing className="h-5 w-5" />
          ) : (
            <Bell className="h-5 w-5" />
          )}

          {unreadCount > 0 && (
            <Badge className="absolute -top-1 -right-1 bg-red-500 text-white text-xs h-5 w-5 flex items-center justify-center p-0 rounded-full">
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="max-h-96 w-[calc(100vw-1rem)] max-w-80 overflow-y-auto border-gray-700 bg-gray-800 text-white"
      >
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="text-xs text-cyan-400 hover:text-cyan-300 p-1 h-auto"
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="bg-gray-700" />

        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-600 mx-auto mb-2"></div>
            <p className="text-gray-400 text-sm">Loading...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-4 text-center">
            <Bell className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.slice(0, 10).map((notification) => (
            <DropdownMenuItem
              key={notification.id}
              className="p-3 cursor-pointer hover:bg-gray-700 focus:bg-gray-700 flex-col items-start"
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="flex items-start gap-3 w-full">
                <div className={`mt-1 ${getNotificationColor(notification.type)}`}>
                  {getNotificationIcon(notification.type)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-medium text-white truncate">
                      {notification.title}
                    </h4>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      {!notification.read && (
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      )}
                      <span className="text-xs text-gray-400">
                        {formatTimeAgo(notification.createdAt)}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-300 line-clamp-2">
                    {notification.message}
                  </p>

                  {/* Job info for job-related notifications */}
                  {(notification.type.startsWith('job_') && notification.data?.jobTitle) && (
                    <div className="mt-1">
                      <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                        {notification.data.jobTitle}
                      </Badge>
                    </div>
                  )}

                  {(notification.type.startsWith('connect') && notification.data?.serviceType) && (
                    <div className="mt-1 flex gap-1 flex-wrap">
                      <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                        {notification.data.serviceType}
                      </Badge>
                      {notification.data.requestDate && (
                        <Badge variant="outline" className="text-xs border-gray-600 text-gray-400">
                          {new Date(notification.data.requestDate).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {!notification.read && notification.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(notification.id!);
                    }}
                    className="text-gray-400 hover:text-white p-1 h-auto"
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </DropdownMenuItem>
          ))
        )}

        {notifications.length > 10 && (
          <>
            <DropdownMenuSeparator className="bg-gray-700" />
            <DropdownMenuItem
              className="text-center text-cyan-400 hover:text-cyan-300 cursor-pointer"
              onClick={() => {
                setLocation('/notifications');
                setIsOpen(false);
              }}
            >
              View all notifications
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
